import React from "react";
import { Avatar, Chip, Stack } from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";

import {
  NetworkPortalLayout,
  PortalAuthState,
  PortalLoading,
  StatusChip,
  TalentPortalCard,
  TalentPortalEmptyState,
  formatDateTime,
  getTalentProfileImageUrl,
  safeArray,
  toTitleCase,
  useNetworkPortalSession,
} from "../network/networkPortalShared";

function DetailList({ items }) {
  return (
    <dl className="talent-profile-details">
      {items.map((item) => (
        <div key={item.label}>
          <dt>{item.label}</dt>
          <dd>{item.value || "Not provided"}</dd>
        </div>
      ))}
    </dl>
  );
}

function TalentMembershipCard({ membership }) {
  const instanceName = membership.originatingCompanyDisplayName || membership.instanceName || "Elite Core organization";
  return (
    <article className="talent-membership-card">
      <div className="talent-membership-card__top">
        <div>
          <h3 className="talent-membership-card__title">{instanceName}</h3>
          <p className="talent-membership-card__meta">
            {toTitleCase(membership.role || "talent")}{membership.instanceUniqueId ? ` • ${membership.instanceUniqueId}` : ""}
          </p>
        </div>
        <StatusChip value={membership.status || "active"} />
      </div>
      <DetailList
        items={[
          { label: "Role", value: toTitleCase(membership.role || "talent") },
          { label: "Instance", value: membership.instanceUniqueId || membership.instanceId },
          { label: "Joined", value: formatDateTime(membership.createdAt) },
          { label: "Updated", value: formatDateTime(membership.updatedAt) },
        ]}
      />
    </article>
  );
}

export default function TalentProfile() {
  const { member, loading, error } = useNetworkPortalSession();

  if (loading) return <PortalLoading />;
  if (!member) return <PortalAuthState error={error} />;

  const memberships = safeArray(member.memberships);
  const verificationState = member.credential?.verifiedAt || member.verifiedAt ? "verified" : "pending";

  return (
    <NetworkPortalLayout title="Profile" subtitle="Manage your Talent account and authorized Core Network memberships." member={member}>
      <div className="talent-portal__grid">
        <div className="talent-portal__grid-main">
          <TalentPortalCard title="Profile summary" subtitle="Your global Talent account details.">
            <div className="talent-profile-hero">
              <Avatar className="talent-profile-hero__avatar" src={getTalentProfileImageUrl(member)} alt={member.displayName || "Talent profile"} />
              <div>
                <h2 className="talent-profile-hero__name">{member.displayName || "Talent member"}</h2>
                <div className="talent-profile-chip-row">
                  <StatusChip value={member.globalStatus || member.accountStatus || "active"} />
                  <Chip size="small" label={toTitleCase(member.memberType || "talent")} />
                  <StatusChip value={verificationState} />
                </div>
              </div>
            </div>
            <DetailList
              items={[
                { label: "Email", value: member.email },
                { label: "Company", value: member.companyName },
                { label: "Member type", value: toTitleCase(member.memberType) },
                { label: "Country", value: member.country },
                { label: "Profile type", value: toTitleCase(member.profileType) },
              ]}
            />
          </TalentPortalCard>

          <TalentPortalCard title="Portal access" subtitle="Credential and account access state returned by Elite Core.">
            <DetailList
              items={[
                { label: "Account status", value: toTitleCase(member.credential?.accountStatus || member.portalStatus || member.globalStatus) },
                { label: "Last login", value: formatDateTime(member.credential?.lastLoginAt || member.portalLastLoginAt) },
                { label: "Password change", value: member.credential?.mustChangePassword || member.mustChangePassword ? "Required at next login" : "Not required" },
                { label: "Temporary password expires", value: formatDateTime(member.credential?.temporaryPasswordExpiresAt || member.temporaryPasswordExpiresAt) },
                { label: "Verified", value: formatDateTime(member.credential?.verifiedAt || member.portalVerifiedAt) },
              ]}
            />
          </TalentPortalCard>
        </div>

        <div className="talent-portal__grid-side">
          <TalentPortalCard title="Authorized memberships" subtitle="Organizations that have granted access to your Core Network Talent profile.">
            <Stack spacing={1.5}>
              {memberships.length ? memberships.map((membership) => (
                <TalentMembershipCard key={membership.id || `${membership.instanceId}-${membership.role}`} membership={membership} />
              )) : (
                <TalentPortalEmptyState
                  icon={<BusinessIcon fontSize="small" />}
                  title="No active memberships"
                  description="You have not yet been granted access to an Elite Core organization."
                />
              )}
            </Stack>
          </TalentPortalCard>
        </div>
      </div>
    </NetworkPortalLayout>
  );
}
